import { MAGIC_FLOW_OPTIONS_URL } from '@/consts/urls';
import { getContext } from '@/context';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';

type Response = {
  status?: string;
  message?: string;
  response?: {
    credits?: number;
    usageAmount?: number;
    monetization?: { toolId?: string };
  };
};

/**
 * Pure request — returns null on network failure or unexpected shape.
 *
 * Quotes the price through `magic-flow-webhook/options`, the `/options` sibling
 * of the `/submit` the generation itself posts to, so the number shown and the
 * job billed come from the same workflow. This replaces
 * `magic-flow-webhook-calculator/execute`, a second workflow whose
 * `result.usageAmount` could drift from what submit would actually charge; it is
 * also the contract the ai-toolkit miniapp prices against
 * (`shared/ai/workflowTransport.ts` → `fetchWorkflowOptions`).
 *
 * `credits` is the price, and is what this returns. The response also carries
 * `usageAmount` (a multiplier, 1 unless stated) and `monetization.toolId`;
 * neither is read here — the offer screen derives its toolId from the surface —
 * but they are typed so the next consumer can see what is on offer.
 */
const getProjectCredits = async ({
  webhookId,
}: {
  webhookId: string;
}): Promise<number | null> => {
  try {
    const response = await getContext().utils.api.fetch(
      MAGIC_FLOW_OPTIONS_URL,
      {
        method: 'POST',
        headers: getAuthJsonHeaders(),
        body: JSON.stringify({ params: { webhookId } }),
      }
    );
    if (!response.ok) return null;
    const data: Response = await response.json();
    if (data.status === 'error') return null;
    return data.response?.credits ?? null;
  } catch {
    return null;
  }
};

export default getProjectCredits;
